import Button from "components/button";
import Card from "components/card";

import React, { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { getUsersList } from "store/orderSlice";
import { createColumnHelper } from "@tanstack/react-table";
import WithoutPageTable from "components/table/WithoutPageTable";
import { FaTrashAlt } from "react-icons/fa";
import { ORDER_TYPE } from "constants";
import { SHIPMENT_TYPE } from "constants";
import { useParams } from "react-router-dom";
import { getEditOrderDetails } from "store/orderSlice";
import Spinner from "components/spinner";
import Radio from "components/radio";
import InputField from "components/fields/InputField";
import { resetEditOrderDetails } from "store/orderSlice";
import DeleteItemModal from "./components/DeleteItemModal";
import OrderStatusModal from "./components/OrderStatusModal";
import AddOrderItem from "./components/AddOrderItem";
import EditItemModal from "./components/EditItemModal";
import { ImagePreview } from "components/modal/ImagePreview";

const columnHelper = createColumnHelper();

function OrderEdit() {
  const { id } = useParams();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(resetEditOrderDetails());
  }, [dispatch]);

  const { order: { draft = {} } = {} } = useSelector(
    (state) => state.auth.permissions || {}
  );
  const { orderEditDetailsLoading, orderEditDetailsData } = useSelector(
    (state) => state.orders
  );

  const [refreshTrigger, setRefreshTrigger] = useState(false);

  const refreshList = () => {
    setRefreshTrigger((prev) => !prev);
  };

  useEffect(() => {
    dispatch(getUsersList({ page: 1, limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(getEditOrderDetails({ id }));
  }, [dispatch, id, refreshTrigger]);

  const {
    control,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      orderType: orderEditDetailsData.orderType || ORDER_TYPE.BASED_ON_DONE,
      shipmentType: orderEditDetailsData.shipmentType || SHIPMENT_TYPE.BY_AIR,
      ...(draft.selectUser
        ? { orderFor: orderEditDetailsData?.orderForUser || "" }
        : {}),
    },
  });
  useEffect(() => {
    if (orderEditDetailsData) {
      reset({
        orderType: orderEditDetailsData.orderType || ORDER_TYPE.BASED_ON_DONE,
        shipmentType: orderEditDetailsData.shipmentType || SHIPMENT_TYPE.BY_AIR,
        ...(draft.selectUser
          ? { orderFor: orderEditDetailsData?.orderForUser || "" }
          : {}),
      });
    }
  }, [orderEditDetailsData]);

  const items = useMemo(() => {
    return orderEditDetailsData?.items || [];
  }, [orderEditDetailsData]);

  const orderType = useMemo(() => {
    return orderEditDetailsData?.orderType || null;
  }, [orderEditDetailsData]);

  // const onSubmit = async (data) => {
  //   if (!draftOrderId) {
  //     showToast.error("Draft order ID is not available!");
  //     return;
  //   }
  //   try {
  //     const payload = {
  //       ...data,
  //       status: draftOrderId,
  //     };
  //     const response = await dispatch(AddNewOrder(payload));
  //     if (
  //       response?.payload?.statuscode === 201 ||
  //       response?.payload?.statuscode === 200
  //     ) {
  //       navigate("/draft-orders");
  //       reset();
  //     } else {
  //       console.error(
  //         "Create failed:",
  //         response?.payload?.msg || "Unknown error"
  //       );
  //     }
  //   } catch (error) {
  //     console.error("Create failed:", error);
  //   }
  // };

  const columns = React.useMemo(() => {
    // two different table shapes
    if (orderType === ORDER_TYPE.CARRYING) {
      return [
        columnHelper.accessor("picture", {
          id: "image",
          header: "IMG",
          enableSorting: false,
          enableHiding: false,
          cell: ({ getValue }) => <ImagePreview url={getValue()} />,
        }),
        columnHelper.accessor("name", {
          id: "name",
          header: "NAME",
          enableSorting: false,
          cell: (info) => info.getValue() || "-",
        }),
        columnHelper.accessor("marka", {
          id: "marka",
          header: "MARKA",
          enableSorting: false,
          cell: (info) => info.getValue() || "-",
        }),
        columnHelper.accessor("ctn", {
          id: "ctn",
          header: "CTN",
          enableSorting: false,
          cell: (info) => info.getValue() ?? 0,
        }),
        columnHelper.accessor("qty", {
          id: "qty",
          header: "QTY/CTN",
          enableSorting: false,
          cell: (info) => info.getValue() ?? 0,
        }),
        columnHelper.accessor("totalQty", {
          id: "totalQty",
          header: "TOTAL QTY",
          enableSorting: false,
          cell: (info) => info.getValue() ?? 0,
        }),
        columnHelper.accessor("rate", {
          id: "rate",
          header: "RATE",
          enableSorting: false,
          cell: (info) => info.getValue() ?? 0,
        }),
        columnHelper.accessor("totalAmount", {
          id: "totalAmount",
          header: "TTL AMT",
          enableSorting: false,
          cell: (info) => info.getValue() ?? 0,
        }),
        columnHelper.accessor("cbm", {
          id: "cbm",
          header: "CBM/CTN",
          enableSorting: false,
          cell: (info) => info.getValue() ?? 0,
        }),
        columnHelper.accessor("totalCBM", {
          id: "totalCBM",
          header: "TTL CBM",
          enableSorting: false,
          cell: (info) => info.getValue() ?? 0,
        }),
        columnHelper.accessor("kg", {
          id: "kg",
          header: "KG/CTN",
          enableSorting: false,
          cell: (info) => info.getValue() ?? 0,
        }),
        columnHelper.accessor("totalKg", {
          id: "totalKg",
          header: "TTL KG",
          enableSorting: false,
          cell: (info) => info.getValue() ?? 0,
        }),
        columnHelper.accessor("actions", {
          id: "actions",
          header: "ACTIONS",
          enableSorting: false,
          cell: ({ row }) => {
            const data = row?.original;
            return (
              <div className="flex gap-2">
                {draft?.itemEdit && (
                  <EditItemModal
                    data={data}
                    refreshList={refreshList}
                    orderType={orderEditDetailsData?.orderType}
                  />
                )}
                {draft?.itemDelete && (
                  <DeleteItemModal data={data} refreshList={refreshList} />
                )}
              </div>
            );
          },
        }),
      ];
    }

    // basedOnDone (default)
    return [
      columnHelper.accessor("picture", {
        id: "image",
        header: "IMG",
        enableSorting: false,
        enableHiding: false,
        cell: ({ getValue }) => <ImagePreview url={getValue()} />,
      }),
      columnHelper.accessor("name", {
        id: "name",
        header: "NAME",
        enableSorting: false,
        cell: (info) => info.getValue() || "Unnamed Item",
      }),
      columnHelper.accessor("description", {
        id: "description",
        header: "DESCRIPTION",
        enableSorting: false,
        cell: (info) => info.getValue() || "-",
      }),
      columnHelper.accessor("totalQty", {
        id: "quantity",
        header: "QTY",
        enableSorting: false,
        cell: (info) => info.getValue() ?? "-",
      }),
      columnHelper.accessor("price", {
        id: "price",
        header: "PRICE",
        enableSorting: false,
        cell: (info) => info.getValue() ?? 0,
      }),
      columnHelper.accessor("actions", {
        id: "actions",
        header: "ACTIONS",
        enableSorting: false,
        cell: ({ row }) => {
          const data = row?.original;
          return (
            <div className="flex gap-2">
              {draft?.itemEdit && (
                <EditItemModal
                  data={data}
                  refreshList={refreshList}
                  orderType={orderEditDetailsData?.orderType}
                />
              )}
              {draft?.itemDelete && (
                <DeleteItemModal data={data} refreshList={refreshList} />
              )}
            </div>
          );
        },
      }),
    ];
  }, [orderType]);

  return (
    <Card extra={"w-full h-full p-3"}>
      <header className="relative flex items-center justify-between">
        <div className="text-xl font-bold text-navy-700 dark:text-white">
          Darft Order
        </div>
      </header>
      {orderEditDetailsLoading ? (
        <div className="flex h-[100px] w-full items-center justify-center p-4">
          <Spinner />
        </div>
      ) : (
        <div className="mt-5">
          <form
            // onSubmit={handleSubmit(onSubmit)}
            className="grid gap-4"
            noValidate
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Controller
                name="orderType"
                control={control}
                rules={{ required: "Order type is required" }}
                render={({ field }) => (
                  <div className="flex flex-col">
                    <label className="ml-1.5 text-sm font-medium text-navy-700 dark:text-white">
                      Order Type*
                    </label>
                    <div className="ml-1.5 mt-2 flex gap-6">
                      <label className="flex items-center gap-2">
                        <Radio
                          value={ORDER_TYPE.BASED_ON_DONE}
                          checked={field.value === ORDER_TYPE.BASED_ON_DONE}
                          onChange={(e) => field.onChange(e.target.value)}
                          name="orderType"
                          disabled={true}
                        />
                        <span>Base on Done</span>
                      </label>

                      <label className="flex items-center gap-2">
                        <Radio
                          value={ORDER_TYPE.CARRYING}
                          checked={field.value === ORDER_TYPE.CARRYING}
                          onChange={(e) => field.onChange(e.target.value)}
                          name="orderType"
                          disabled={true}
                        />
                        <span>Carrying</span>
                      </label>
                    </div>
                    {errors.orderType && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.orderType.message}
                      </p>
                    )}
                  </div>
                )}
              />

              <Controller
                name="shipmentType"
                control={control}
                rules={{ required: "Shipment type is required" }}
                render={({ field }) => (
                  <div className="flex flex-col">
                    <label className="ml-1.5 text-sm font-medium text-navy-700 dark:text-white">
                      Shipment Type*
                    </label>
                    <div className="ml-1.5 mt-2 flex gap-6">
                      <label className="flex items-center gap-2">
                        <Radio
                          value={SHIPMENT_TYPE.BY_AIR}
                          checked={field.value === SHIPMENT_TYPE.BY_AIR}
                          onChange={(e) => field.onChange(e.target.value)}
                          name="shipmentType"
                          disabled={true}
                        />
                        <span>By Air</span>
                      </label>

                      <label className="flex items-center gap-2">
                        <Radio
                          value={SHIPMENT_TYPE.BY_SEA}
                          checked={field.value === SHIPMENT_TYPE.BY_SEA}
                          onChange={(e) => field.onChange(e.target.value)}
                          name="shipmentType"
                          disabled={true}
                        />
                        <span>By Sea</span>
                      </label>
                    </div>
                    {errors.shipmentType && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.shipmentType.message}
                      </p>
                    )}
                  </div>
                )}
              />
              {draft.selectUser && (
                <Controller
                  name="orderFor"
                  control={control}
                  rules={{ required: "Order For is required" }}
                  render={({ field, fieldState }) => (
                    <InputField
                      id="orderFor"
                      variant="auth"
                      label="Order For*"
                      placeholder="Enter Order For"
                      type="text"
                      value={field.value || ""}
                      disabled
                    />
                  )}
                />
              )}
            </div>
          </form>
          {/* Items */}

          <div className="mt-3">
            {draft?.itemAdd && (
              <AddOrderItem
                orderId={orderEditDetailsData?._id}
                refreshList={refreshList}
                orderType={orderEditDetailsData?.orderType}
              />
            )}
          </div>
          {items.length > 0 && (
            <WithoutPageTable title="Items" columns={columns} data={items} />
          )}
          <div className="mt-3 flex justify-end gap-2">
            {draft?.statusChange && (
              <OrderStatusModal
                data={orderEditDetailsData}
                refreshList={refreshList}
                isRedirect={true}
              />
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

export default OrderEdit;
